import { ANALYTICS_SCREEN_NAMES, APP_ENTRY_VALUES, APP_PLATFORM_VALUES, } from "./analytics-taxonomy.js";
import { conciergeRequestTypes } from "./concierge-request-requirements.js";
import { AnalyticsParameterError } from "./customer-analytics-window.js";
// The metric engine of Explore (ADR 0017, decision 11): the closed list of metrics, what each one measures, where its numbers come from, and the
// rules that the server checks before it reads anything. Pure: no database, no clock. The repository reads, the use case applies these rules.
//
// The list adds `customer_messages` and `notifications_sent` to the twelve metrics of the ADR table. Both are record metrics with no split. The
// Overview shows both numbers as tiles, and a tile that is clickable needs a metric to open.
export const ANALYTICS_METRICS = [
    "sessions",
    "time_in_app",
    "screens_per_session",
    "screen_time",
    "active_days",
    "requests_created",
    "requests_completed",
    "payments_paid",
    "amount_paid",
    "trips_created",
    "saved_items_added",
    "handoffs_requested",
    "customer_messages",
    "notifications_sent",
];
export const ANALYTICS_INTERVAL_VALUES = ["hour", "day", "week"];
export const ANALYTICS_SPLITS = ["none", "platform", "entry", "screen", "request_type"];
export const ANALYTICS_METRIC_DEFINITIONS = Object.freeze({
    sessions: { unit: "count", source: "VISITS", splits: ["none", "platform", "entry"] },
    time_in_app: { unit: "seconds", source: "VISITS", splits: ["none", "platform", "entry"] },
    screens_per_session: { unit: "ratio", source: "VISITS", splits: ["none", "platform"] },
    screen_time: { unit: "seconds", source: "VISIT_SCREENS", splits: ["none", "screen"] },
    active_days: { unit: "count", source: "VISITS", splits: ["none"] },
    requests_created: { unit: "count", source: "RECORDS", splits: ["none", "request_type"] },
    requests_completed: { unit: "count", source: "RECORDS", splits: ["none", "request_type"] },
    payments_paid: { unit: "count", source: "RECORDS", splits: ["none"] },
    amount_paid: { unit: "minor_inr", source: "RECORDS", splits: ["none"] },
    trips_created: { unit: "count", source: "RECORDS", splits: ["none"] },
    saved_items_added: { unit: "count", source: "RECORDS", splits: ["none"] },
    handoffs_requested: { unit: "count", source: "RECORDS", splits: ["none"] },
    customer_messages: { unit: "count", source: "RECORDS", splits: ["none"] },
    notifications_sent: { unit: "count", source: "RECORDS", splits: ["none"] },
});
export const isAnalyticsMetric = (value) => ANALYTICS_METRICS.includes(value);
/** A metric of the visit read model (consent applies to it). A record metric answers for every customer who passes the gate. */
export const isAppMetric = (metric) => ANALYTICS_METRIC_DEFINITIONS[metric].source !== "RECORDS";
export const bucketRuleOf = (metric) => isAppMetric(metric) ? "VISIT_START" : "RECORD_TIME";
/** The keys that a split can take. A visit with no known start has the platform or the entry `unknown`. `other` holds the series past the limit. */
export const ANALYTICS_SPLIT_KEYS = {
    none: ["all"],
    platform: [...APP_PLATFORM_VALUES, "unknown"],
    entry: [...APP_ENTRY_VALUES, "unknown"],
    screen: [...ANALYTICS_SCREEN_NAMES],
    request_type: [...conciergeRequestTypes],
};
/** At most this many series in a split, `other` included (decision 11). */
export const ANALYTICS_MAX_SERIES = 5;
export const ANALYTICS_OTHER_KEY = "other";
const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
/** `hour` needs a span of 7 days or less, `day` 400 days or less; `week` has no limit (decision 11). */
const INTERVAL_MAX_SPAN_MS = {
    hour: 7 * DAY_MS,
    day: 400 * DAY_MS,
    week: Number.POSITIVE_INFINITY,
};
/** The interval a chart starts with when the caller names none: hours for two days or less, days up to 45 days, weeks beyond. */
export function defaultAnalyticsInterval(window) {
    const span = window.to.getTime() - window.from.getTime();
    if (span <= 2 * DAY_MS)
        return "hour";
    return span <= 45 * DAY_MS ? "day" : "week";
}
/** The split must be on the list of the metric. Otherwise `analytics.split_not_allowed`. */
export function assertAllowedSplit(metric, split) {
    if (!ANALYTICS_METRIC_DEFINITIONS[metric].splits.includes(split)) {
        throw new AnalyticsParameterError("analytics.split_not_allowed");
    }
}
/** The span of the window must fit the bucket limit of the interval. Otherwise `analytics.interval_not_allowed`. */
export function assertAllowedInterval(interval, window) {
    if (window.to.getTime() - window.from.getTime() > INTERVAL_MAX_SPAN_MS[interval]) {
        throw new AnalyticsParameterError("analytics.interval_not_allowed");
    }
}
/**
 * The previous period: the same span directly before the window (decision 11). `range=all` with no zoom has none (`NO_PERIOD`). An app metric
 * cuts it at the retention cutoff: a period wholly before the cutoff is `RETENTION`, one that crosses it is `partial` with `coveredFrom`.
 * A record metric is kept in full, so it is never cut.
 */
export function resolvePreviousPeriod(input) {
    if (input.range === "all" && !input.zoomed)
        return { status: "NO_PERIOD" };
    const span = input.to.getTime() - input.from.getTime();
    const from = new Date(input.from.getTime() - span);
    const to = input.from;
    if (!input.app)
        return { status: "AVAILABLE", from, to, coveredFrom: from, partial: false };
    if (to.getTime() <= input.retentionCutoff.getTime())
        return { status: "RETENTION" };
    const cut = from.getTime() < input.retentionCutoff.getTime();
    return {
        status: "AVAILABLE",
        from,
        to,
        coveredFrom: cut ? input.retentionCutoff : from,
        partial: cut,
    };
}
/** The relative change `(value - previous) / previous`, or `null` when there is nothing to compare with (no previous value, or zero). */
export function relativeChange(value, previous) {
    if (previous === null || previous === 0)
        return null;
    return (value - previous) / previous;
}
